import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  Avatar,
  Box,
  Stack,
  SxProps,
  Text,
  useTheme,
} from '@ltvco/refresh-lib/theme';
import { useAccount, useSession } from '@ltvco/refresh-lib/v1';
import {
  Close,
  InsertDriveFile as InsertDriveFileIcon,
  Person as PersonIcon,
  QuestionAnswer,
} from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { type ReactNode, useContext, useState, useRef } from 'react';

import {
  BrandLogoChat,
  NewChatButton,
  ChatPageBoxContainer,
  ProfileModal,
  HelpModal,
} from 'components/ChatBV/components';
import {
  CHATBV_GA4_EVENTS,
  FEEDBACK_FORM_URL,
} from 'components/ChatBV/constants';
import { ChatContext } from 'components/ChatBV/context';

import { ConversationList } from '../../Sidebar/ChatHistory/ConversationList';
import { ProfileSection } from '../../Sidebar/ProfileSection';

interface MobileNavItemProps {
  children: ReactNode;
  onClick?: () => void;
  sx?: SxProps;
}

const MobileNavItem = ({ children, onClick, sx }: MobileNavItemProps) => (
  <Stack
    direction="row"
    spacing={2}
    alignItems="center"
    onClick={onClick}
    sx={{
      marginBottom: '32px !important',
      ...sx,
    }}
  >
    {children}
  </Stack>
);

interface ChatPageSidebarMobileProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onRecentReportsClick?: () => void;
  onCloseRecentReports?: () => void;
  onUpgradePlanClick?: () => void;
  onAccountClick?: () => void;
  onLogout?: () => void;
}

export const ChatPageSidebarMobile = ({
  isOpen,
  setIsOpen,
  onRecentReportsClick,
  onCloseRecentReports,
  onUpgradePlanClick,
  onAccountClick,
  onLogout,
}: ChatPageSidebarMobileProps) => {
  const theme = useTheme();
  const [openProfileModal, setOpenProfileModal] = useState(false);
  const [openHelpModal, setOpenHelpModal] = useState(false);
  const [isProfileHovered, setIsProfileHovered] = useState(false);
  const { handleNewChat } = useContext(ChatContext);
  const { trackEventGA4 } = useContext(AppConfig);
  const {
    session: { isAuthenticated },
  } = useSession();
  const { data: account } = useAccount(isAuthenticated);
  const profileRef = useRef<HTMLDivElement>(null);

  const handleNewChatClick = () => {
    handleNewChat();
    onCloseRecentReports?.();
    setIsOpen(false);
  };

  const handleRecentReportsClick = () => {
    onRecentReportsClick?.();
    setIsOpen(false); // Close mobile sidebar when opening recent reports
  };

  const handleAccountClick = () => {
    onAccountClick?.();
    setIsOpen(false);
  };

  const handleProfileClick = () => {
    setOpenProfileModal(true);
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: 'profile_modal',
    });
  };

  const handleProfileModalClose = () => {
    setOpenProfileModal(false);
    setIsOpen(false); // Close sidebar when modal closes
  };

  const handleHelpModalClick = () => {
    setOpenHelpModal(true);
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: CHATBV_GA4_EVENTS.navSidebar.subTypes.help_modal,
    });
  };

  const handleFeedbackFormClick = () => {
    window.open(FEEDBACK_FORM_URL, '_blank');
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: CHATBV_GA4_EVENTS.navSidebar.subTypes.feedback_form,
    });
  };

  const handleUpgradePlanClick = () => {
    onUpgradePlanClick?.();
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: CHATBV_GA4_EVENTS.navSidebar.subTypes.upgrade_plan,
    });
  };

  const getInitial = () => {
    const firstName = account?.account?.user_info?.first_name;
    const lastName = account?.account?.user_info?.last_name;
    const email = account?.account?.user_info?.email;

    if (firstName) {
      return firstName[0].toUpperCase();
    }
    if (lastName) {
      return lastName[0].toUpperCase();
    }
    if (email) {
      return email[0].toUpperCase();
    }
    return 'U';
  };

  return (
    <>
      <ChatPageBoxContainer
        position="fixed"
        top={0}
        left={0}
        width="100%"
        height="100vh"
        boxShadow="4px 0 8px rgba(0, 0, 0, 0.15)"
        zIndex={1000}
        isAnimatingOut={!isOpen}
      >
        <Stack
          spacing={3}
          padding={3}
          height="100%"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Stack
            alignItems="center"
            spacing={2}
            marginBottom={4}
            flexDirection="row"
            justifyContent="space-between"
            width="100%"
            sx={{ flexShrink: 0 }}
          >
            <BrandLogoChat width="116px" height="16px" />
            <Close
              onClick={() => {
                setIsOpen(false);
              }}
              sx={{
                marginTop: '0 !important',
              }}
            />
          </Stack>

          <Stack spacing={2} sx={{ flexShrink: 0 }}>
            <MobileNavItem onClick={handleNewChatClick}>
              <NewChatButton variant="contained">
                <AddIcon
                  sx={{
                    width: '20px',
                    height: '20px',
                    color: theme.palette.text.contrastText,
                  }}
                />
              </NewChatButton>
              <Text fontWeight="bold" color={theme.palette.primary.main}>
                New Chat
              </Text>
            </MobileNavItem>
            <MobileNavItem onClick={handleRecentReportsClick}>
              <InsertDriveFileIcon
                sx={{
                  width: '20px',
                  height: '20px',
                  color: theme.palette.text.secondary,
                }}
              />
              <Text fontWeight="bold" color={theme.palette.primary.main}>
                Report Library
              </Text>
            </MobileNavItem>
          </Stack>

          <Stack
            spacing={0.5}
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{
                marginTop: '8px',
                marginBottom: 2,
                flexShrink: 0,
              }}
            >
              <QuestionAnswer
                sx={{
                  width: '20px',
                  height: '20px',
                  color: theme.palette.text.secondary,
                }}
              />
              <Text fontWeight="bold" color={theme.palette.primary.main}>
                Chat History
              </Text>
            </Stack>
            <Stack
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                WebkitOverflowScrolling: 'touch',
                bgcolor: 'background.default',
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  height: 16,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  bgcolor: 'background.default',
                }}
              />
              <Box sx={{ flexShrink: 0, minHeight: 'min-content' }}>
                <ConversationList
                  collapsed={false}
                  fullWidth={true}
                  onConversationClick={() => setIsOpen(false)}
                />
              </Box>
            </Stack>
          </Stack>

          <Stack spacing={2} sx={{ flexShrink: 0 }}>
            {/* Account Section */}
            <MobileNavItem onClick={handleAccountClick}>
              <PersonIcon
                sx={{
                  width: '20px',
                  height: '20px',
                  marginTop: '-1px !important',
                  color: theme.palette.text.secondary,
                }}
              />
              <Text
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.primary.main,
                }}
              >
                Account
              </Text>
            </MobileNavItem>
            {/* Profile Section */}
            <Box
              ref={profileRef}
              onClick={handleProfileClick}
              onMouseEnter={() => setIsProfileHovered(true)}
              onMouseLeave={() => setIsProfileHovered(false)}
              sx={{
                padding: '8px',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  sx={{
                    backgroundColor: 'primary.main',
                    fontSize: '12px',
                    width: '24px',
                    height: '24px',
                  }}
                >
                  {getInitial()}
                </Avatar>
                <ProfileSection
                  account={account}
                  isHovered={isProfileHovered}
                />
              </Stack>
            </Box>
          </Stack>
        </Stack>
      </ChatPageBoxContainer>
      <HelpModal open={openHelpModal} onClose={() => setOpenHelpModal(false)} />
      <ProfileModal
        open={openProfileModal}
        onClose={handleProfileModalClose}
        onUpgradePlanClick={handleUpgradePlanClick}
        onHelpClick={handleHelpModalClick}
        onFeedbackClick={handleFeedbackFormClick}
        onLogout={onLogout}
      />
    </>
  );
};
